import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reactionsService, ReactionType } from '@/services/reactions.service';

export const reactionKeys = {
  all: ['reactions'] as const,
  lessonStats: (lessonId: string) => [...reactionKeys.all, 'lesson', lessonId, 'stats'] as const,
  commentCount: (commentId: string) => [...reactionKeys.all, 'comment', commentId, 'count'] as const,
  myReactions: (lessonId?: string) =>
    lessonId
      ? [...reactionKeys.all, 'me', lessonId] as const
      : [...reactionKeys.all, 'me'] as const,
};

export function useLessonReactionStats(lessonId: string) {
  return useQuery({
    queryKey: reactionKeys.lessonStats(lessonId),
    queryFn: () => reactionsService.getLessonStats(lessonId),
    staleTime: 10000, // 10 seconds
  });
}

export function useCommentReactionCount(commentId: string) {
  return useQuery({
    queryKey: reactionKeys.commentCount(commentId),
    queryFn: () => reactionsService.getCommentReactionCount(commentId),
    staleTime: 10000,
  });
}

export function useMyReactions(lessonId?: string) {
  return useQuery({
    queryKey: reactionKeys.myReactions(lessonId),
    queryFn: () => reactionsService.getMyReactions(lessonId),
    staleTime: 30000,
  });
}

export function useToggleLessonReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, type }: { lessonId: string; type?: ReactionType }) =>
      reactionsService.toggleLessonReaction(lessonId, type),
    onMutate: async ({ lessonId }) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({ queryKey: reactionKeys.lessonStats(lessonId) });
      await queryClient.cancelQueries({ queryKey: reactionKeys.myReactions(lessonId) });

      // Snapshot do estado anterior
      const previousStats = queryClient.getQueryData(reactionKeys.lessonStats(lessonId));
      const previousMyReactions = queryClient.getQueryData(reactionKeys.myReactions(lessonId));

      return { previousStats, previousMyReactions };
    },
    onError: (error, { lessonId }, context) => {
      // Reverter para o estado anterior em caso de erro
      if (context?.previousStats) {
        queryClient.setQueryData(reactionKeys.lessonStats(lessonId), context.previousStats);
      }
      if (context?.previousMyReactions) {
        queryClient.setQueryData(reactionKeys.myReactions(lessonId), context.previousMyReactions);
      }
    },
    onSettled: (_, __, { lessonId }) => {
      // Sempre refetch após a mutação
      queryClient.invalidateQueries({ queryKey: reactionKeys.lessonStats(lessonId) });
      queryClient.invalidateQueries({ queryKey: reactionKeys.myReactions(lessonId) });
    },
  });
}

export function useToggleCommentReaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, type }: { commentId: string; type?: ReactionType }) =>
      reactionsService.toggleCommentReaction(commentId, type),
    onMutate: async ({ commentId }) => {
      await queryClient.cancelQueries({ queryKey: reactionKeys.commentCount(commentId) });
      const previousCount = queryClient.getQueryData(reactionKeys.commentCount(commentId));
      return { previousCount };
    },
    onError: (error, { commentId }, context) => {
      if (context?.previousCount) {
        queryClient.setQueryData(reactionKeys.commentCount(commentId), context.previousCount);
      }
    },
    onSettled: (_, __, { commentId }) => {
      queryClient.invalidateQueries({ queryKey: reactionKeys.commentCount(commentId) });
      queryClient.invalidateQueries({ queryKey: reactionKeys.myReactions() });
    },
  });
}
